import React from 'react'
import { DollarSign, Receipt, Clock, AlertTriangle, RotateCcw, XCircle, ChevronLeft, ChevronRight } from 'lucide-react'

export default function InvoicesTab({
  invoicesData,
  invoicesLoading,
  invoicesSummary,
  invPage,
  setInvPage,
  invStatus,
  setInvStatus,
  invActionLoading,
  handleRefund,
  handleCancelInvoice
}) {
  const formatCurrency = (cents) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((cents || 0) / 100)
  const formatDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'
  
  const getInvoiceStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">Paid</span>
      case 'pending': return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs">Pending</span>
      case 'overdue': return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs">Overdue</span>
      case 'cancelled': return <span className="px-2 py-1 bg-zinc-800 text-zinc-200 rounded-full text-xs">Cancelled</span>
      case 'refunded': return <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full text-xs">Refunded</span>
      default: return <span className="px-2 py-1 bg-zinc-800 text-zinc-200 rounded-full text-xs">{status}</span>
    }
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-zinc-800 rounded-xl p-5 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Total Revenue</p>
              <p className="text-xl font-bold">{formatCurrency(invoicesSummary?.revenue?.total || 0)}</p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl p-5 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/100/20 flex items-center justify-center">
              <Receipt className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">This Month</p>
              <p className="text-xl font-bold">{formatCurrency(invoicesSummary?.revenue?.this_month || 0)}</p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl p-5 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Pending</p>
              <p className="text-xl font-bold">{formatCurrency(invoicesSummary?.revenue?.pending || 0)}</p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800 rounded-xl p-5 border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Overdue</p>
              <p className="text-xl font-bold">{invoicesSummary?.status_counts?.overdue || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <select
          value={invStatus}
          onChange={(e) => { setInvStatus(e.target.value); setInvPage(1) }}
          className="px-4 py-2 border rounded-lg"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="cancelled">Cancelled</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {/* Invoices Table */}
      <div className="bg-zinc-800 rounded-2xl border overflow-hidden">
        {invoicesLoading ? (
          <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div></div>
        ) : invoicesData?.invoices?.length === 0 ? (
          <div className="p-12 text-center text-zinc-400">No invoices found</div>
        ) : (
          <>
            <table className="w-full">
              <thead className="bg-zinc-900 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Invoice</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoicesData?.invoices?.map((inv) => (
                  <tr key={inv.invoice_id} className="hover:bg-zinc-900">
                    <td className="px-6 py-4">
                      <p className="font-medium text-sm">{inv.invoice_id?.slice(0, 8)}...</p>
                      <p className="text-xs text-zinc-400">{inv.description || 'Invoice'}</p>
                    </td>
                    <td className="px-6 py-4 font-medium">{inv.team_name}</td>
                    <td className="px-6 py-4">
                      <p className="font-medium">{inv.user_name}</p>
                      <p className="text-xs text-zinc-400">{inv.user_email}</p>
                    </td>
                    <td className="px-6 py-4 font-bold">{formatCurrency(inv.amount)}</td>
                    <td className="px-6 py-4">{getInvoiceStatusBadge(inv.status)}</td>
                    <td className="px-6 py-4 text-sm text-zinc-400">{formatDate(inv.created_at)}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {inv.status === 'paid' && (
                          <button onClick={() => handleRefund(inv.invoice_id)} disabled={invActionLoading === inv.invoice_id}
                            className="p-2 text-purple-400 hover:bg-purple-500/10 rounded-lg disabled:opacity-50" title="Refund">
                            <RotateCcw className="w-4 h-4" />
                          </button>
                        )}
                        {inv.status === 'pending' && (
                          <button onClick={() => handleCancelInvoice(inv.invoice_id)} disabled={invActionLoading === inv.invoice_id}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg disabled:opacity-50" title="Cancel">
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {invoicesData?.pages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <p className="text-sm text-zinc-400">Page {invPage} of {invoicesData.pages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setInvPage(p => Math.max(1, p - 1))} disabled={invPage === 1} className="p-2 border rounded-lg disabled:opacity-50">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setInvPage(p => Math.min(invoicesData.pages, p + 1))} disabled={invPage === invoicesData.pages} className="p-2 border rounded-lg disabled:opacity-50">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
